import React, { useCallback, useMemo, useState } from 'react';
import { Description, DivRelative, SubTitle, Image, Mask } from '../../components/layouts';
import Sequence from '../../components/sequence';
import { buildImageURL, getSeqFrames, pureTrans } from '../../helpers';
import Visibility from '../../components/Visibility';
import './style.scss';
import { AppContext } from '../../Context';
import { SeqencePlaceholder } from '../../components/sequence/Placeholder';

const IMAGES = {
  placeholder: {
    d: buildImageURL('/zeiss-placeholder.jpg'),
    m: buildImageURL('/zeiss-placeholder-m.jpg')
  },
  logo: buildImageURL('/vivo_zeiss_logo.png')
};

export function VivoZeiss() {
  const [frozen, freeze] = useState(true);
  const [framesLoaded, setFramesLoaded] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [progress, setProgress] = useState(0);
  const [height, setHeight] = useState(0);

  const { webpSupport, webpDetectionFinished } = React.useContext(AppContext);

  const frames = useMemo(() => {
    return getSeqFrames({
      prefix: 'eye',
      mobile,
      webpSupport: false,
      ext: 'jpg',
      len: 110
    });
  }, [webpSupport, mobile]);

  const { index, a1, a2, b1, b2, c1, c2, d1, afterMaskOpacity } = useMemo(() => {
    const shift = mobile ? -50 : -50;
    const shiftedTarget = [shift + 10, shift, shift - 10];
    return {
      index: pureTrans(progress, [0.24, 0.72], [0, frames.length - 1], 0),
      a1: pureTrans(progress, [-0.06, 0.06, 0.12], [0, 1, 0], 3),
      a2: pureTrans(progress, [0, 0.06, 0.12], shiftedTarget, 3),
      b1: pureTrans(progress, [0.12, 0.18, 0.24], [0, 1, 0], 3),
      b2: pureTrans(progress, [0.12, 0.18, 0.24], shiftedTarget, 3),
      c1: pureTrans(progress, [0.72, 0.84], [0, 1], 3),
      c2: pureTrans(progress, [0.72, 0.78, 0.84], shiftedTarget, 3),
      afterMaskOpacity: pureTrans(progress, [0.48, 0.72], [0, 0.6], 3),
      d1: pureTrans(progress, [-0.06, 0.12], [0, 1], 3)
    };
  }, [progress, mobile, frames]);

  const anchorY = useMemo(() => {
    const top = 0.875 * height;
    return top;
  }, [height]);

  const handleUpdate = useCallback(
    (e, { calculations }) => {
      const { percentagePassed, pixelsPassed, height, offScreen, width } = calculations;
      freeze(offScreen || pixelsPassed <= -1000 || pixelsPassed >= height + 1000);
      setMobile(width <= 750);
      setHeight(height);
      setProgress(percentagePassed);
    },
    [setProgress, setHeight, freeze]
  );

  const onFramesLoaded = useCallback(() => {
    setFramesLoaded(true);
  }, [setFramesLoaded]);

  const maskVisible = !framesLoaded || index === 0;
  return (
    <Visibility fireOnMount offset={[-1000, -1000]} onUpdate={handleUpdate} className="page-section section-zeiss inverted" id="zeiss">
      <div id="camera-platform" style={{ position: 'absolute', top: anchorY }} />
      <div className="sticky-content">
        <Mask opacity={afterMaskOpacity} />
        <DivRelative>
          <div className="section-content s-c-1" style={{ opacity: a1, transform: `translate(-50%, ${a2}%) translateZ(0)` }}>
            <Image src={IMAGES.logo} className="logo" />
          </div>

          <div className="section-content s-c-2" style={{ opacity: b1, transform: `translate(-50%, ${b2}%) translateZ(0)` }}>
            <SubTitle>强强联手</SubTitle>
            <Description>
              X60系列搭载vivo蔡司联合影像系统，配备蔡司光学镜头，使得产品的摄影功能更加强悍。在复现景象本原之美的同时，打造定制级专业影像。
            </Description>
          </div>

          <div className="section-content s-c-3" style={{ opacity: c1, transform: `translate(-50%, ${c2}%) translateZ(0)` }}>
            <SubTitle>第二代微云台</SubTitle>
            <Description>微云台通过相机镜头反向运动补偿，可稳定拍摄画面；采用4800万主摄，搭配超大光圈，轻松捕捉精彩瞬间。</Description>
          </div>
        </DivRelative>

        <div className="section-background">
          <div className="sequence-wrapper">
            <Sequence
              current={index}
              frames={frames}
              onload={onFramesLoaded}
              active={!frozen && webpDetectionFinished}
              visible={!maskVisible}
            />
            <SeqencePlaceholder
              src={mobile ? IMAGES.placeholder.m : IMAGES.placeholder.d}
              visible={maskVisible}
              opaicty={maskVisible ? d1 : 0}
            />
          </div>
        </div>
      </div>
    </Visibility>
  );
}
